import React, { useRef, useEffect, useState, useMemo } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import HoverPlugin from 'wavesurfer.js/dist/plugins/hover.esm.js';
import { Play, Pause, Volume2, VolumeX, ZoomIn, ZoomOut } from 'lucide-react';

const SECTION_COLORS = {
  intro:     'rgba(59, 130, 246, 0.18)',   // blue
  verse:     'rgba(148, 163, 184, 0.18)',  // slate
  buildup:   'rgba(234, 179, 8, 0.22)',    // amber
  build:     'rgba(234, 179, 8, 0.22)',
  drop:      'rgba(37, 99, 255, 0.30)',    // brand blue
  chorus:    'rgba(37, 99, 255, 0.22)',
  breakdown: 'rgba(168, 85, 247, 0.22)',   // purple
  bridge:    'rgba(168, 85, 247, 0.20)',
  outro:     'rgba(34, 197, 94, 0.20)',    // green
};

const sectionLabel = (type) => (type || 'seg').toLowerCase();
const sectionColor = (type) => SECTION_COLORS[sectionLabel(type)] || 'rgba(100, 116, 139, 0.18)';

/**
 * Waveform v2 — adds:
 *   • Structure regions (Intro / Build / Drop / Outro) overlaid as colored bands
 *   • Hover-time tooltip
 *   • Zoom in/out, gradient bars, finer resolution
 *
 * Props
 *   audioUrl        : string  — the track audio URL
 *   height          : number  — pixel height of the canvas
 *   structure       : Array<{type, start|start_time, duration|end_time, energy}>
 *   waveColor / progressColor / cursorColor — visual overrides
 */
export default function Waveform({
  audioUrl,
  height = 120,
  waveColor = '#CBD5E1',
  progressColor = '#2563FF',
  cursorColor = '#14E6C9',
  structure = [],
}) {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [zoom, setZoom] = useState(0);

  // Normalise structure into a stable list of {type, start, end, label}
  const normalised = useMemo(() => {
    if (!Array.isArray(structure)) return [];
    return structure
      .map((s) => {
        const start = s.start ?? s.start_time ?? 0;
        let end = s.end ?? s.end_time;
        if (end == null && s.duration != null) end = start + s.duration;
        if (end == null || end <= start) return null;
        return { type: s.type || 'seg', start, end, label: sectionLabel(s.type) };
      })
      .filter(Boolean);
  }, [structure]);

  useEffect(() => {
    // No audio → skip wavesurfer init; the static structure rail below
    // is rendered instead so the upgrade is still visible.
    if (!containerRef.current || !audioUrl) {
      setIsLoading(false);
      return;
    }

    setHasError(false);
    setIsLoading(true);

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 2,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height,
      normalize: true,
      backend: 'WebAudio',
      plugins: [
        regions,
        HoverPlugin.create({
          lineColor: '#2563FF',
          lineWidth: 1,
          labelBackground: '#0F172A',
          labelColor: '#FFFFFF',
          labelSize: '11px',
        }),
      ],
    });
    wavesurferRef.current = ws;
    ws.load(audioUrl);

    ws.on('ready', () => {
      setIsLoading(false);
      setDuration(ws.getDuration());
      ws.setVolume(volume);
      // Paint structure regions once the buffer is loaded
      normalised.forEach((seg) => {
        try {
          regions.addRegion({
            start: seg.start,
            end: seg.end,
            color: sectionColor(seg.type),
            drag: false,
            resize: false,
            content: seg.label,
          });
        } catch { /* malformed segment, skip */ }
      });
    });

    ws.on('audioprocess', () => setCurrentTime(ws.getCurrentTime()));
    ws.on('seeking', () => setCurrentTime(ws.getCurrentTime()));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => setIsPlaying(false));
    ws.on('error', (err) => {
      // eslint-disable-next-line no-console
      console.error('Waveform error:', err);
      setHasError(true);
      setIsLoading(false);
    });

    return () => {
      try { ws.destroy(); } catch { /* already destroyed */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, height, waveColor, progressColor, cursorColor, normalised]);

  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(isMuted ? 0 : volume);
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (wavesurferRef.current && zoom >= 0) {
      try { wavesurferRef.current.zoom(zoom); } catch { /* no-op */ }
    }
  }, [zoom]);

  const togglePlayPause = () => wavesurferRef.current?.playPause();
  const toggleMute = () => setIsMuted((m) => !m);
  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setIsMuted(v === 0);
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  // Legend pulls the unique section types actually present
  const legend = useMemo(() => {
    const seen = new Set();
    return normalised
      .map((s) => s.label)
      .filter((l) => (seen.has(l) ? false : (seen.add(l), true)));
  }, [normalised]);

  return (
    <div className="w-full" data-testid="waveform-root">
      {/* Section legend */}
      {legend.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="waveform-legend">
          {legend.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider text-[#0F172A] border border-[#E2E8F0]"
              style={{ background: sectionColor(label) }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: sectionColor(label).replace(/[\d.]+\)$/, '0.85)') }} />
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Waveform canvas — or static structure preview if no audio */}
      {audioUrl ? (
        <div
          ref={containerRef}
          data-testid="waveform-canvas"
          className="w-full bg-gradient-to-b from-white to-[#F8FAFC] border border-[#E2E8F0] rounded-xl overflow-hidden"
          style={{ minHeight: height }}
        >
          {isLoading && !hasError && (
            <div className="w-full flex items-center justify-center" style={{ height }}>
              <div className="flex items-center gap-2 text-[#64748B]">
                <div className="w-4 h-4 border-2 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Decoding waveform…</span>
              </div>
            </div>
          )}
          {hasError && (
            <div className="w-full flex items-center justify-center text-xs text-[#94A3B8]" style={{ height }}>
              Audio not available — try re-uploading
            </div>
          )}
        </div>
      ) : (
        // Static structure-only preview (no playable audio)
        <StaticStructureRail height={height} structure={normalised} />
      )}

      {/* Controls — only when we have actual audio to play */}
      {audioUrl && !isLoading && !hasError && (
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <button
            onClick={togglePlayPause}
            className="w-10 h-10 flex items-center justify-center gradient-primary text-white rounded-full hover:opacity-90 transition-opacity shadow-[0_4px_12px_rgba(37,99,255,0.25)]"
            data-testid="waveform-play-btn"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>

          <div className="font-mono text-sm text-[#64748B]">
            <span className="text-[#0F172A]">{formatTime(currentTime)}</span>
            <span className="mx-1">/</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => setZoom((z) => Math.max(0, z - 20))}
              className="w-7 h-7 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/40 flex items-center justify-center"
              data-testid="waveform-zoom-out"
              title="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setZoom((z) => Math.min(400, z + 20))}
              className="w-7 h-7 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/40 flex items-center justify-center"
              data-testid="waveform-zoom-in"
              title="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={toggleMute}
              className="text-[#64748B] hover:text-[#0F172A]"
              data-testid="waveform-mute-btn"
            >
              {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-20 h-1 bg-[#DBEAFE] rounded-lg appearance-none cursor-pointer accent-[#2563FF]"
              data-testid="waveform-volume-slider"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * StaticStructureRail — fallback when no audio is available. Renders the
 * structure segments as proportionally-sized colored bands on a horizontal
 * rail with section labels, so DJs still get the structural overview.
 */
function StaticStructureRail({ height, structure }) {
  if (!structure || structure.length === 0) {
    return (
      <div
        data-testid="waveform-canvas-static"
        className="w-full bg-gradient-to-b from-white to-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-center justify-center text-xs text-[#94A3B8]"
        style={{ minHeight: height }}
      >
        Upload the audio file to see the playable waveform
      </div>
    );
  }
  const total = structure[structure.length - 1].end;
  return (
    <div
      data-testid="waveform-canvas-static"
      className="w-full bg-gradient-to-b from-white to-[#F8FAFC] border border-[#E2E8F0] rounded-xl overflow-hidden relative"
      style={{ minHeight: height }}
    >
      <div className="absolute inset-0 flex" aria-label="Track structure preview">
        {structure.map((seg, i) => {
          const widthPct = ((seg.end - seg.start) / total) * 100;
          return (
            <div
              key={i}
              data-testid={`waveform-static-segment-${seg.label}`}
              className="relative flex items-end justify-center border-r border-white/60 last:border-r-0"
              style={{ width: `${widthPct}%`, background: sectionColor(seg.type) }}
              title={`${seg.label} · ${Math.round(seg.start)}s – ${Math.round(seg.end)}s`}
            >
              {widthPct > 6 && (
                <span className="absolute top-1 left-2 text-[10px] font-mono uppercase tracking-wider text-[#0F172A]/70">
                  {seg.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="absolute bottom-1 right-2 text-[10px] font-mono text-[#64748B]/80 pointer-events-none">
        STRUCTURE PREVIEW · audio not available
      </div>
    </div>
  );
}
